<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
  watch,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment, cacheBust } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  cacheBust: {
    type: Boolean,
    default: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['playError']);

defineOptions({
  inheritAttrs: false,
});

const sourceURL = computed(() => attachment.dataUrl);
const timeStampURL = computed(() => {
  return cacheBust ? timeStampAppendedURL(sourceURL.value) : sourceURL.value;
});

const TRANSCRIPT_PREVIEW_LENGTH = 200;
const isTranscriptExpanded = ref(false);
const isTranscriptLong = computed(
  () => (attachment.transcribedText?.length || 0) > TRANSCRIPT_PREVIEW_LENGTH
);
const displayedTranscript = computed(() => {
  const text = attachment.transcribedText || '';
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);
const playAttemptId = ref(0);
const playPending = ref(false);
const isUnmounted = ref(false);
const playError = ref(false);

const { uid } = getCurrentInstance();

// MediaRecorder-produced WebM/Opus blobs lack a Duration header → <audio>.duration
// resolves to Infinity until we seek past the end, which forces the engine to
// scan the file and compute the real length. Safe no-op for files with a real
// duration already (mp3/m4a/etc).
let clearDurationProbe = null;
const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el || clearDurationProbe) return;
  const onDurationResolved = () => {
    if (!Number.isFinite(el.duration) || el.duration > 1e6) return;
    clearDurationProbe?.();
    el.currentTime = 0;
    currentTime.value = 0;
    duration.value = el.duration;
  };
  clearDurationProbe = () => {
    el.removeEventListener('timeupdate', onDurationResolved);
    el.removeEventListener('durationchange', onDurationResolved);
    clearDurationProbe = null;
  };
  el.addEventListener('timeupdate', onDurationResolved);
  el.addEventListener('durationchange', onDurationResolved);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    clearDurationProbe();
  }
};

const onLoadedMetadata = () => {
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d) || d > 1e6) {
    resolveStreamingDuration();
    return;
  }
  duration.value = d;
};

// The <source> element inside <audio> is not reactive to src changes: the
// browser only reconsiders sources when .load() is called explicitly on the
// parent <audio>. A real-time message can have its dataUrl change after the
// initial mount (async transcoding, attachment still hydrating over
// ActionCable), so without this the player keeps pointing at whichever
// source the browser saw when it first parsed the element.
watch(
  timeStampURL,
  () => {
    clearDurationProbe?.();
    duration.value = 0;
    currentTime.value = 0;
    const el = audioPlayer.value;
    if (!el) return;
    const wasPlaying = isPlaying.value;
    playAttemptId.value += 1;
    playPending.value = false;
    isPlaying.value = false;
    playError.value = false;
    el.load();
    if (wasPlaying) {
      // eslint-disable-next-line no-use-before-define
      playAudio();
    }
  },
  { flush: 'post' }
);

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  isUnmounted.value = false;
  const d = audioPlayer.value?.duration;
  if (Number.isFinite(d)) duration.value = d;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid) {
    playAttemptId.value += 1;
    playPending.value = false;
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  if (clearDurationProbe) return;
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const onMediaError = () => {
  if (isUnmounted.value || playError.value) return;
  playAttemptId.value += 1;
  playPending.value = false;
  isPlaying.value = false;
  playError.value = true;
  clearDurationProbe?.();
  emit('playError', { error: audioPlayer.value?.error });
};

const playAudio = async () => {
  const el = audioPlayer.value;
  if (!el) return;
  playAttemptId.value += 1;
  const attemptId = playAttemptId.value;
  playPending.value = true;
  const retry = playError.value;
  playError.value = false;
  try {
    if (retry) {
      el.load();
      el.playbackRate = playbackSpeed.value;
    }
    await el.play();
    if (attemptId !== playAttemptId.value || isUnmounted.value) {
      return;
    }
    isPlaying.value = true;
  } catch (error) {
    if (attemptId === playAttemptId.value && !isUnmounted.value) {
      isPlaying.value = false;
      playError.value = true;
      emit('playError', { error });
    }
  } finally {
    if (attemptId === playAttemptId.value) playPending.value = false;
  }
};

const playOrPause = async () => {
  if (isPlaying.value) {
    playAttemptId.value += 1;
    playPending.value = false;
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else if (!playPending.value) {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    await playAudio();
  }
};

const onEnd = () => {
  playAttemptId.value += 1;
  playPending.value = false;
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};

onBeforeUnmount(() => {
  clearDurationProbe?.();
  const el = audioPlayer.value;
  if (el) {
    try {
      el.pause();
    } catch {
      /* ignore pause errors during teardown */
    }
  }
});

onUnmounted(() => {
  isUnmounted.value = true;
  playAttemptId.value += 1;
  playPending.value = false;
});
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="onLoadedMetadata"
    @durationchange="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @error="onMediaError"
  >
    <source :src="timeStampURL" @error="onMediaError" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-2xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex w-full flex-col gap-1.5">
      <div class="flex min-w-0 items-center gap-1.5">
        <button
          type="button"
          class="inline-flex size-11 shrink-0 items-center justify-center rounded-xl border-0 p-0"
          :aria-label="
            $t(
              isPlaying
                ? 'INTERNAL_CHAT.AUDIO.PAUSE'
                : 'INTERNAL_CHAT.AUDIO.PLAY'
            )
          "
          @click="playOrPause"
        >
          <Icon v-if="isPlaying" class="size-4" icon="i-ph-pause" />
          <Icon v-else class="size-4" icon="i-ph-play" />
        </button>
        <div class="flex min-w-0 flex-1 items-center px-2">
          <input
            type="range"
            min="0"
            :max="duration"
            :value="currentTime"
            :aria-label="$t('INTERNAL_CHAT.AUDIO.SEEK')"
            class="reset-base !m-0 h-11 w-full cursor-pointer appearance-none rounded-xl !border-0 bg-transparent !p-0 accent-current [&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-current [&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-current [&::-moz-range-track]:h-1 [&::-moz-range-track]:rounded-xl [&::-moz-range-track]:bg-n-slate-12/40 [&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:rounded-xl [&::-webkit-slider-runnable-track]:bg-n-slate-12/40"
            @input="seek"
          />
        </div>
        <div
          class="shrink-0 whitespace-nowrap tabular-nums text-xs text-current"
        >
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </div>
      </div>
      <div class="flex items-center justify-end gap-1.5">
        <button
          type="button"
          class="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl border-0 bg-n-alpha-2 hover:bg-alpha-3"
          :aria-label="$t('INTERNAL_CHAT.AUDIO.CHANGE_SPEED')"
          @click="changePlaybackSpeed"
        >
          <span class="text-xs text-current font-medium">
            {{ playbackSpeedLabel }}
          </span>
        </button>
        <button
          type="button"
          class="inline-flex size-11 shrink-0 items-center justify-center rounded-xl border-0 p-0"
          :aria-label="
            $t(
              isMuted
                ? 'INTERNAL_CHAT.AUDIO.UNMUTE'
                : 'INTERNAL_CHAT.AUDIO.MUTE'
            )
          "
          @click="toggleMute"
        >
          <Icon v-if="isMuted" class="size-4" icon="i-ph-speaker-slash" />
          <Icon v-else class="size-4" icon="i-ph-speaker-high" />
        </button>
        <button
          type="button"
          class="inline-flex size-11 shrink-0 items-center justify-center rounded-xl border-0 p-0"
          :aria-label="$t('INTERNAL_CHAT.AUDIO.DOWNLOAD')"
          @click="downloadAudio"
        >
          <Icon class="size-4" icon="i-ph-download-simple" />
        </button>
      </div>
    </div>
    <p
      v-if="playError"
      role="alert"
      class="m-0 w-full px-2 text-xs text-n-ruby-11"
    >
      {{ $t('INTERNAL_CHAT.AUDIO.PLAY_ERROR') }}
    </p>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-xl w-full break-words"
    >
      {{ displayedTranscript }}
      <button
        v-if="isTranscriptLong"
        type="button"
        class="mt-1 inline-flex min-h-11 items-center border-0 bg-transparent p-0 text-n-slate-11 hover:text-n-slate-12 font-medium"
        :aria-label="
          $t(
            isTranscriptExpanded
              ? 'CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS'
              : 'CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE'
          )
        "
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{
          isTranscriptExpanded
            ? $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS')
            : $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE')
        }}
      </button>
    </div>
  </div>
</template>
